/*
 * A small markdown renderer for wiki pages.
 *
 * Deliberately a subset, not a library: what these pages contain is headings,
 * paragraphs, lists, tables, fenced code, and `[[wikilinks]]` — and the wikilinks
 * are the reason a renderer is needed at all. A page whose links are inert text is
 * a page you cannot navigate, and navigating between pages is most of what reading
 * a wiki is. Everything unrecognised falls through as a paragraph, so an unhandled
 * construct is plain text rather than a blank.
 *
 * Link resolution is done against the page index, so a link to a page that does not
 * exist renders as broken. That is a lint finding made visible while reading, rather
 * than a dead end discovered by clicking.
 */
import type { ReactNode } from 'react'

interface MarkdownProps {
  content: string
  /** Page paths that exist, used to tell a live wikilink from a broken one. */
  known?: Set<string>
  onOpenLink?: (target: string) => void
}

export function Markdown({ content, known, onOpenLink }: MarkdownProps) {
  return <>{renderBlocks(content, known, onOpenLink)}</>
}

function renderBlocks(
  content: string,
  known: Set<string> | undefined,
  onOpenLink: ((target: string) => void) | undefined,
): ReactNode[] {
  const lines = content.split('\n')
  const out: ReactNode[] = []
  let index = 0
  let key = 0

  while (index < lines.length) {
    const line = lines[index] ?? ''

    if (line.trim() === '') {
      index += 1
      continue
    }

    // fenced code
    if (line.trimStart().startsWith('```')) {
      const body: string[] = []
      index += 1
      while (index < lines.length && !(lines[index] ?? '').trimStart().startsWith('```')) {
        body.push(lines[index] ?? '')
        index += 1
      }
      index += 1
      out.push(
        <pre key={key++}>
          <code>{body.join('\n')}</code>
        </pre>,
      )
      continue
    }

    const heading = /^(#{1,4})\s+(.*)$/.exec(line)
    if (heading) {
      const level = (heading[1] ?? '').length
      const text = inline(heading[2] ?? '', known, onOpenLink)
      out.push(
        level <= 1 ? (
          <h1 key={key++}>{text}</h1>
        ) : level === 2 ? (
          <h2 key={key++}>{text}</h2>
        ) : (
          <h3 key={key++}>{text}</h3>
        ),
      )
      index += 1
      continue
    }

    // table: a header row followed by a delimiter row
    if (
      line.includes('|') &&
      index + 1 < lines.length &&
      /^[\s|:-]+$/.test(lines[index + 1] ?? '')
    ) {
      const header = splitRow(line)
      index += 2
      const rows: string[][] = []
      while (index < lines.length && (lines[index] ?? '').includes('|')) {
        rows.push(splitRow(lines[index] ?? ''))
        index += 1
      }
      out.push(
        <div className="table-scroll" key={key++}>
          <table>
            <thead>
              <tr>
                {header.map((cell, cellIndex) => (
                  <th key={cellIndex}>{inline(cell, known, onOpenLink)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, rowIndex) => (
                <tr key={rowIndex}>
                  {row.map((cell, cellIndex) => (
                    <td key={cellIndex}>{inline(cell, known, onOpenLink)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>,
      )
      continue
    }

    if (/^\s*>/.test(line)) {
      const body: string[] = []
      while (index < lines.length && /^\s*>/.test(lines[index] ?? '')) {
        body.push((lines[index] ?? '').replace(/^\s*>\s?/, ''))
        index += 1
      }
      out.push(<blockquote key={key++}>{inline(body.join(' '), known, onOpenLink)}</blockquote>)
      continue
    }

    const bullet = /^\s*[-*+]\s+/
    const ordered = /^\s*\d+[.)]\s+/
    if (bullet.test(line) || ordered.test(line)) {
      const isOrdered = ordered.test(line)
      const items: string[] = []
      while (
        index < lines.length &&
        (bullet.test(lines[index] ?? '') || ordered.test(lines[index] ?? ''))
      ) {
        items.push((lines[index] ?? '').replace(bullet, '').replace(ordered, ''))
        index += 1
      }
      const rendered = items.map((item, itemIndex) => (
        <li key={itemIndex}>{inline(item, known, onOpenLink)}</li>
      ))
      out.push(isOrdered ? <ol key={key++}>{rendered}</ol> : <ul key={key++}>{rendered}</ul>)
      continue
    }

    // paragraph: consume until a blank line or a construct starts
    const paragraph: string[] = []
    while (index < lines.length) {
      const next = lines[index] ?? ''
      if (
        next.trim() === '' ||
        /^(#{1,4})\s/.test(next) ||
        bullet.test(next) ||
        ordered.test(next) ||
        next.trimStart().startsWith('```') ||
        /^\s*>/.test(next)
      ) {
        break
      }
      paragraph.push(next)
      index += 1
    }
    out.push(<p key={key++}>{inline(paragraph.join(' '), known, onOpenLink)}</p>)
  }

  return out
}

function splitRow(line: string): string[] {
  return line
    .replace(/^\s*\|/, '')
    .replace(/\|\s*$/, '')
    .split('|')
    .map((cell) => cell.trim())
}

/** `[[wikilink]]`, `` `code` ``, `**bold**` and citation markers. */
const INLINE_RE = /(\[\[[^\]]+\]\]|`[^`]+`|\*\*[^*]+\*\*|\^\[[^\]]+\])/g

function inline(
  text: string,
  known: Set<string> | undefined,
  onOpenLink: ((target: string) => void) | undefined,
): ReactNode[] {
  const parts = text.split(INLINE_RE)
  return parts.map((part, index) => {
    if (part === undefined) return null
    if (part.startsWith('[[') && part.endsWith(']]')) {
      const target = part.slice(2, -2).trim()
      const resolved = resolveTarget(target, known)
      const broken = known !== undefined && resolved === null
      return (
        <button
          key={index}
          type="button"
          className={`wikilink${broken ? ' wikilink--broken' : ''}`}
          title={broken ? 'Страницы с таким именем нет' : resolved ?? target}
          disabled={!onOpenLink || broken}
          onClick={() => resolved && onOpenLink?.(resolved)}
        >
          {target}
        </button>
      )
    }
    if (part.startsWith('`') && part.endsWith('`') && part.length > 1) {
      return <code key={index}>{part.slice(1, -1)}</code>
    }
    if (part.startsWith('**') && part.endsWith('**') && part.length > 3) {
      return <strong key={index}>{part.slice(2, -2)}</strong>
    }
    if (part.startsWith('^[') && part.endsWith(']')) {
      return (
        <code key={index} title="Цитата: файл и диапазон строк, которые lint перечитывает">
          {part}
        </code>
      )
    }
    return part
  })
}

/**
 * Map a wikilink target onto a page path.
 *
 * A link is written by title or slug (`[[inference-throughput]]`) and pages live at
 * real paths (`wiki/concepts/inference-throughput.md`), so resolution is a suffix
 * match on the basename. Ambiguity resolves to the first match, which is the same
 * thing the alias registry exists to prevent from arising.
 */
export function resolveTarget(target: string, known: Set<string> | undefined): string | null {
  if (!known) return target
  if (known.has(target)) return target
  const slug = target.replace(/\.md$/, '').toLowerCase()
  for (const path of known) {
    const base = path.replace(/\.md$/, '').split('/').pop()?.toLowerCase()
    if (base === slug) return path
  }
  return null
}

/** Wikilink targets a page's body refers to, used for outbound-link fallbacks. */
export function extractLinks(content: string): string[] {
  const links = new Set<string>()
  for (const match of content.matchAll(/\[\[([^\]]+)\]\]/g)) {
    if (match[1]) links.add(match[1].trim())
  }
  return [...links]
}
